// client/src/components/common/pickerHooks.ts
//
// Hooks shared by the date/time pickers (ST-00204, ST-00294).
//
//  - usePickerSheetMode: true on a phone-width viewport (< 640 px, Tailwind's `sm`) or a coarse
//    (touch) primary pointer, where the pickers open as a bottom sheet (PickerSheet) instead of
//    the desktop popup anchored to the field (PickerPopover). No matchMedia (jsdom, old
//    browsers) reads as desktop.
//  - useViewportWidthAtLeast: whether the window is at least so wide, following resizes (the
//    desktop range popup shows two months only where they fit).
//  - useSequencedRangeCommit: hands a new start and end to a page as two callbacks, the end
//    only after the page has re-rendered with the start. Pages build the second update from
//    their render-time state (`setParams({ to })` from the current URL, `{ from: period.from,
//    to }`), so two calls in one tick let the second overwrite the first.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const PICKER_SHEET_QUERY = '(max-width: 639.98px), (pointer: coarse)';

const readSheetMode = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return !!window.matchMedia(PICKER_SHEET_QUERY)?.matches;
  } catch {
    return false;
  }
};

export function usePickerSheetMode(): boolean {
  const [sheet, setSheet] = useState(readSheetMode);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let mq: MediaQueryList | undefined;
    try {
      mq = window.matchMedia(PICKER_SHEET_QUERY);
    } catch {
      return undefined;
    }
    if (!mq) return undefined;
    const list = mq;
    const onChange = () => setSheet(!!list.matches);
    onChange();
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    }
    // Safari < 14 only has the deprecated listener API.
    list.addListener?.(onChange);
    return () => list.removeListener?.(onChange);
  }, []);
  return sheet;
}

const viewportWidth = (): number =>
  typeof window === 'undefined'
    ? 0
    : document.documentElement?.clientWidth || window.innerWidth || 0;

export function useViewportWidthAtLeast(minWidth: number): boolean {
  const [wide, setWide] = useState(() => viewportWidth() >= minWidth);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setWide(viewportWidth() >= minWidth);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minWidth]);
  return wide;
}

/** A ref that always holds the latest value (updated before effects run). */
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * `commit(start, end)` calls `onStart(start)` at once and `onEnd(end)` after the next commit,
 * through the page's newest callback. Either side may be left `undefined` to skip it; a lone
 * end is passed straight away.
 */
export function useSequencedRangeCommit<T>(onStart: (v: T) => void, onEnd: (v: T) => void) {
  const latest = useLatest({ onStart, onEnd });
  const queued = useRef<{ value: T } | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const next = queued.current;
    if (!next) return;
    queued.current = null;
    latest.current.onEnd(next.value);
  }, [tick, latest]);

  return useCallback(
    (start: T | undefined, end: T | undefined) => {
      if (start !== undefined) latest.current.onStart(start);
      if (end === undefined) return;
      if (start === undefined) {
        latest.current.onEnd(end);
        return;
      }
      queued.current = { value: end };
      setTick((t) => t + 1);
    },
    [latest]
  );
}

/**
 * Esc while `active` runs `onEscape` — taken on `window` in the capture phase so a picker
 * inside a dialog (the LexiStar Modal closes on a document capture listener) closes itself,
 * not the dialog under it.
 */
export function useEscapeKey(active: boolean, onEscape: () => void) {
  const latest = useLatest(onEscape);
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      latest.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [active, latest]);
}

/**
 * After a day is tapped in a date + time sheet, scroll the time steppers into view if they sit
 * below the fold of the sheet (smoothly, unless the user asked for reduced motion).
 */
export function revealTime(node: HTMLElement | null) {
  if (!node || typeof node.scrollIntoView !== 'function') return;
  const reduce =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    !!window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
  // After the tap's re-render, so the sheet has its final height.
  setTimeout(() => node.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' }), 0);
}
